/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import dayjs from "dayjs";
import { ApprovalMode, ExecutionStatus } from "~/generated/graphql";
import { isNil, isNotEmpty } from "~/tools";
import { EventEntity } from "../../types";
import { ApprovalBadgeKind, ApprovalBadgeMeta } from "./types";

// Keep aligned with executor approval checker polling interval (1 minute).
export const APPROVAL_CHECKER_WAIT_INTERVAL_MINUTES = 1;

export function isApprovalRequiredAction(eventEntity: EventEntity): boolean {
  // Hide the badge if the latest attempt was already approved — it means the action
  // is approved and transitioning (PENDING → IN_PROGRESS), or a rerun that was
  // just approved. Status may still lag briefly after approval.
  if (eventEntity.globalState?.approvedAt) {
    return false;
  }
  return eventEntity.globalState?.status === ExecutionStatus.ApprovalRequired;
}

export function isApprovalRequiredActionBatch(
  eventEntity: EventEntity
): boolean {
  return eventEntity.status === ExecutionStatus.ApprovalRequired;
}

export function getApprovalBadgeMeta(
  eventEntity: EventEntity
): ApprovalBadgeMeta {
  const { approvalMode, windowDuration } = eventEntity;

  if (approvalMode === ApprovalMode.Whenever) {
    return { kind: ApprovalBadgeKind.noDeadline, icon: undefined };
  }

  const startTime = getEventEntityStartTime(eventEntity);

  // No reliable start time — server returns "" when it cannot estimate (e.g. predecessor
  // skipWindow != NEVER, or approvalMode WHENEVER on a chain member).
  if (!startTime) {
    return {
      kind: ApprovalBadgeKind.pending,
      icon: "clock",
    };
  }

  if (isNil(windowDuration)) {
    return { kind: ApprovalBadgeKind.noDeadline, icon: undefined };
  }

  const expireAt = dayjs(startTime).add(
    windowDuration - APPROVAL_CHECKER_WAIT_INTERVAL_MINUTES,
    "minute"
  );

  return {
    kind: ApprovalBadgeKind.leftTime,
    expireAt: expireAt.toDate(),
    icon: "clock",
  };
}

export function getEventEntityStartTime(eventEntity: EventEntity) {
  if (eventEntity.actions && isNotEmpty(eventEntity.actions)) {
    return (
      eventEntity.startTime ||
      eventEntity.actions[0].globalState?.startTime ||
      eventEntity.actions[0].globalState?.estimatedStartTime
    );
  }

  return (
    eventEntity.globalState?.startTime ||
    eventEntity.globalState?.estimatedStartTime
  );
}
